<template>
  <div
    :class="['notification-card', { 'is-unread': isUnread, 'is-mandatory': notification.mandatory, 'is-expanded': expanded }]">
    <button
      type="button"
      class="notification-card__row"
      @click="toggleExpand">
      <span
        class="notification-card__rail"
        :aria-hidden="!isUnread"></span>
      <span class="notification-card__icon-wrap">
        <q-icon
          :name="cardIcon"
          class="notification-card__icon" />
      </span>
      <span class="notification-card__body">
        <span class="notification-card__title">{{ notification.title }}</span>
        <span class="notification-card__meta">
          <span class="notification-card__time">{{ relativeTime }}</span>
          <span
            v-if="priorityLabel"
            :class="['notification-card__chip', `is-${priorityTone}`]">
            {{ priorityLabel }}
          </span>
          <span
            v-if="notification.mandatory"
            class="notification-card__chip is-mandatory">
            {{ t('tabs.mandatory') }}
          </span>
        </span>
      </span>
      <span class="notification-card__actions">
        <button
          v-if="canOpenModel"
          type="button"
          class="notification-card__icon-btn"
          :aria-label="t('actions.view')"
          @click.stop="openModel">
          <q-icon :name="symRoundedArrowOutward" />
          <q-tooltip :delay="350">{{ t('actions.view') }}</q-tooltip>
        </button>
        <button
          v-if="hasExpandableContent"
          type="button"
          :class="['notification-card__icon-btn', 'notification-card__chevron', { 'is-rotated': expanded }]"
          :aria-label="expanded ? t('actions.collapse') : t('actions.expand')"
          @click.stop="toggleExpand">
          <q-icon :name="symRoundedKeyboardArrowDown" />
        </button>
        <button
          type="button"
          class="notification-card__icon-btn"
          :aria-label="t('actions.more')"
          @click.stop>
          <q-icon :name="symRoundedMoreVert" />
          <q-menu auto-close>
            <q-list dense>
              <q-item
                v-if="notification.read_at"
                clickable
                @click="markUnread">
                <q-item-section avatar>
                  <q-icon :name="symRoundedRadioButtonUnchecked" />
                </q-item-section>
                <q-item-section>{{ t('actions.markUnread') }}</q-item-section>
              </q-item>
              <q-item
                v-else
                clickable
                @click="markRead">
                <q-item-section avatar>
                  <q-icon :name="symRoundedCheckCircle" />
                </q-item-section>
                <q-item-section>{{ t('actions.markRead') }}</q-item-section>
              </q-item>
              <q-item
                v-if="notification.is_progressive && notification.progress_status === 'failed'"
                clickable
                @click="retryAction">
                <q-item-section avatar>
                  <q-icon :name="symRoundedRefresh" />
                </q-item-section>
                <q-item-section>{{ t('actions.retry') }}</q-item-section>
              </q-item>
              <q-item
                v-if="notification.is_progressive && progressIsActive"
                clickable
                @click="cancelAction">
                <q-item-section avatar>
                  <q-icon :name="symRoundedClose" />
                </q-item-section>
                <q-item-section>{{ t('actions.cancel') }}</q-item-section>
              </q-item>
              <q-separator />
              <q-item
                clickable
                @click="deleteNotification">
                <q-item-section avatar>
                  <q-icon
                    :name="symRoundedDelete"
                    color="negative" />
                </q-item-section>
                <q-item-section>{{ t('actions.delete') }}</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </button>
      </span>
    </button>

    <div
      v-show="expanded"
      class="notification-card__details">
      <div
        v-if="notification.message"
        class="notification-card__message">
        {{ notification.message }}
      </div>

      <component
        :is="dynamicRendererComponent"
        v-if="dynamicRendererComponent"
        :notification="notification" />

      <div
        v-if="!dynamicRendererComponent && notification.is_progressive && notification.progress_data"
        class="notification-card__progress">
        <template v-if="notification.progress_status !== 'completed' && !notification.hide_progress_bar">
          <div class="notification-card__progress-stage">
            {{ t('progress.stage', { stage: notification.progress_data.stage }) }}
          </div>
          <q-linear-progress
            :value="getProgressValue()"
            :color="getProgressColor()"
            size="6px"
            rounded
            class="q-my-xs" />
          <div class="notification-card__progress-pct">
            {{ t('progress.title', { progress: Math.round(getProgressValue() * 100) }) }}
          </div>
          <div
            v-if="notification.progress_data.message"
            class="notification-card__progress-msg">
            {{ notification.progress_data.message }}
          </div>
        </template>
        <div
          v-if="notification.progress_status === 'completed' && notification.completed_at"
          class="notification-card__progress-state is-completed">
          <q-icon
            :name="symRoundedCheckCircle"
            class="q-mr-xs" />
          {{ t('progress.completed', { time: formatTime(notification.completed_at) }) }}
        </div>
        <div
          v-if="notification.progress_status === 'failed' && notification.progress_data.error"
          class="notification-card__progress-state is-failed">
          <q-icon
            :name="symRoundedError"
            class="q-mr-xs" />
          {{ t('progress.failed', { error: notification.progress_data.error }) }}
        </div>
      </div>

      <div
        v-if="hasFiles"
        class="notification-card__files">
        <q-btn
          v-if="notification.reports?.length"
          flat
          dense
          no-caps
          size="sm"
          :icon="symRoundedDescription"
          :label="t('files.reports', { count: notification.reports.length })"
          class="notification-card__file-btn"
          @click.stop>
          <q-menu>
            <q-list style="min-width: 220px">
              <q-item-label header>{{ t('files.downloadReports') }}</q-item-label>
              <q-item
                v-for="file in notification.reports"
                :key="file.id"
                clickable
                @click="downloadFile(file)">
                <q-item-section avatar>
                  <q-icon :name="getFileIcon(file)" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ file.file_name || file.name }}</q-item-label>
                  <q-item-label caption>{{ formatFileSize(file.size) }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
        <q-btn
          v-if="notification.attachments?.length"
          flat
          dense
          no-caps
          size="sm"
          :icon="symRoundedAttachFile"
          :label="t('files.attachments', { count: notification.attachments.length })"
          class="notification-card__file-btn"
          @click.stop>
          <q-menu>
            <q-list style="min-width: 220px">
              <q-item-label header>{{ t('files.downloadAttachments') }}</q-item-label>
              <q-item
                v-for="file in notification.attachments"
                :key="file.id"
                clickable
                @click="downloadFile(file)">
                <q-item-section avatar>
                  <q-icon :name="getFileIcon(file)" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ file.file_name || file.name }}</q-item-label>
                  <q-item-label caption>{{ formatFileSize(file.size) }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
        <q-btn
          v-if="notification.documents?.length"
          flat
          dense
          no-caps
          size="sm"
          :icon="symRoundedDescription"
          :label="t('files.documents', { count: notification.documents.length })"
          class="notification-card__file-btn"
          @click.stop>
          <q-menu>
            <q-list style="min-width: 220px">
              <q-item-label header>{{ t('files.downloadDocuments') }}</q-item-label>
              <q-item
                v-for="file in notification.documents"
                :key="file.id"
                clickable
                @click="downloadFile(file)">
                <q-item-section avatar>
                  <q-icon :name="getFileIcon(file)" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ file.file_name || file.name }}</q-item-label>
                  <q-item-label caption>{{ formatFileSize(file.size) }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </div>

      <div
        v-if="availableActions.length > 0"
        class="notification-card__action-buttons">
        <q-btn
          v-for="action in availableActions"
          :key="action.name"
          unelevated
          dense
          no-caps
          size="sm"
          :color="action.color || 'primary'"
          :icon="action.icon"
          :label="action.label"
          :loading="executingAction === action.name"
          :disable="action.disabled"
          @click.stop="executeAction(action.name, action)" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  symRoundedArrowOutward,
  symRoundedAttachFile,
  symRoundedCheckCircle,
  symRoundedClose,
  symRoundedDelete,
  symRoundedDescription,
  symRoundedError,
  symRoundedImage,
  symRoundedKeyboardArrowDown,
  symRoundedMoreVert,
  symRoundedNotifications,
  symRoundedPictureAsPdf,
  symRoundedPriorityHigh,
  symRoundedRadioButtonUnchecked,
  symRoundedRefresh,
} from '@quasar/extras/material-symbols-rounded'
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import useDate from 'src/CrudManager/Core/composables/useDate'
import { computed, ref } from 'vue'
import { useRendererRegistry } from '../composables/useRendererRegistry'
import type { Notification, NotificationAction, NotificationFile } from '../types'

interface Props {
  notification: Notification
}

interface Emits {
  (e: 'click', notification: Notification): void
  (e: 'mark-read', notification: Notification): void
  (e: 'mark-unread', notification: Notification): void
  (e: 'delete', notification: Notification): void
  (e: 'execute-action', notification: Notification, actionName: string, action: NotificationAction): void
  (e: 'retry', notification: Notification): void
  (e: 'cancel', notification: Notification): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useCompositeI18n('core.NotificationCenter')
const { formatTime, getRelativeTime } = useDate()
const { get: getRenderer } = useRendererRegistry()

const expanded = ref(false)
const executingAction = ref<string | null>(null)

const isUnread = computed(() => !props.notification.read_at)

const priorityLabel = computed(() => {
  const p = props.notification.priority
  if (!p || p === 'normal') return ''
  return t(`priority.${p}`)
})

const priorityTone = computed(() => {
  switch (props.notification.priority) {
    case 'urgent':
      return 'urgent'
    case 'high':
      return 'high'
    case 'low':
      return 'low'
    default:
      return 'normal'
  }
})

const cardIcon = computed(() => {
  if (props.notification.mandatory) return symRoundedPriorityHigh
  return symRoundedNotifications
})

const relativeTime = computed(() =>
  getRelativeTime(props.notification.updated_at || props.notification.created_at),
)

const dynamicRendererComponent = computed(() => {
  if (!props.notification.data?.custom_renderer) return null
  return getRenderer(props.notification.data.custom_renderer)
})

const availableActions = computed(() => {
  if (!props.notification.data?.actions) return []
  return Object.entries(props.notification.data.actions)
    .filter(([, action]) => isActionAvailable(action))
    .map(([name, action]) => ({ name, ...action }))
    .slice(0, 3)
})

const hasFiles = computed(() => {
  const n = props.notification
  return (n.reports?.length || 0) + (n.attachments?.length || 0) + (n.documents?.length || 0) > 0
})

const hasExpandableContent = computed(() => {
  return (
    !!dynamicRendererComponent.value
    || hasFiles.value
    || availableActions.value.length > 0
    || !!props.notification.message
    || (props.notification.is_progressive && !!props.notification.progress_data)
  )
})

const canOpenModel = computed(() => !!(props.notification.model_type && props.notification.model_id))

const progressIsActive = computed(() =>
  ['pending', 'processing'].includes(props.notification.progress_status || ''),
)

const isActionAvailable = (action: NotificationAction) => {
  if (action.disabled) return false
  if (action.show_when && props.notification.progress_status) {
    return action.show_when === props.notification.progress_status
  }
  return true
}

const toggleExpand = () => {
  if (!hasExpandableContent.value) return
  expanded.value = !expanded.value
}

const openModel = () => {
  emit('click', props.notification)
}

const markRead = () => emit('mark-read', props.notification)
const markUnread = () => emit('mark-unread', props.notification)
const deleteNotification = () => emit('delete', props.notification)
const retryAction = () => emit('retry', props.notification)
const cancelAction = () => emit('cancel', props.notification)

const executeAction = async (actionName: string, action: NotificationAction) => {
  executingAction.value = actionName
  try {
    emit('execute-action', props.notification, actionName, action)
  } finally {
    executingAction.value = null
  }
}

const getProgressValue = () => {
  if (!props.notification.progress_data?.progress) return 0
  return Math.max(0, Math.min(1, props.notification.progress_data.progress / 100))
}

const getProgressColor = () => {
  switch (props.notification.progress_status) {
    case 'completed':
      return 'positive'
    case 'failed':
      return 'negative'
    case 'cancelled':
      return 'grey-6'
    case 'processing':
      return 'primary'
    default:
      return 'grey-4'
  }
}

const getFileIcon = (file: NotificationFile) => {
  if (file.mime_type?.startsWith('image/')) return symRoundedImage
  if (file.mime_type === 'application/pdf') return symRoundedPictureAsPdf
  return symRoundedDescription
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const downloadFile = (file: NotificationFile) => {
  const downloadUrl = file.original_url || file.download_url || file.url
  if (!downloadUrl) {
    console.warn('No download URL available for file:', file.name)
    return
  }
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = file.file_name || file.name
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
</script>

<style lang="scss" scoped>
.notification-card {
  position: relative;
  border-radius: 10px;
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  & + & {
    margin-top: 2px;
  }

  &.is-mandatory {
    background: color-mix(in srgb, var(--q-warning) 8%, transparent);
  }
}

.notification-card__row {
  display: grid;
  grid-template-columns: 4px 32px 1fr auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 10px;
  border: none;
  background: transparent;
  border-radius: 10px;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.05);

    .notification-card__icon-btn {
      opacity: 0.85;
    }
  }
}

.body--light .notification-card__row:hover {
  background: rgba(0, 0, 0, 0.04);
}

.notification-card__rail {
  width: 4px;
  height: 22px;
  border-radius: 999px;
  background: transparent;
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-card.is-unread .notification-card__rail {
  background: var(--q-primary);
}

.notification-card.is-mandatory .notification-card__rail {
  background: var(--q-warning);
}

.notification-card__icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.body--light .notification-card__icon-wrap {
  background: rgba(0, 0, 0, 0.05);
}

.notification-card.is-mandatory .notification-card__icon-wrap {
  background: color-mix(in srgb, var(--q-warning) 22%, transparent);
  color: var(--q-warning);
}

.notification-card.is-unread:not(.is-mandatory) .notification-card__icon-wrap {
  background: color-mix(in srgb, var(--q-primary) 18%, transparent);
  color: var(--q-primary);
}

.notification-card__icon {
  font-size: 1.05rem;
  opacity: 0.9;
}

.notification-card__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.notification-card__title {
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-card.is-unread .notification-card__title {
  color: #ffffff;
}

.body--light .notification-card.is-unread .notification-card__title {
  color: #0f1419;
}

.notification-card__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  color: $grey-6;
  flex-wrap: wrap;
}

.notification-card__time {
  opacity: 0.85;
}

.notification-card__chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  border: 1px solid transparent;

  &.is-urgent {
    background: color-mix(in srgb, var(--q-negative) 16%, transparent);
    color: var(--q-negative);
    border-color: color-mix(in srgb, var(--q-negative) 30%, transparent);
  }

  &.is-high {
    background: color-mix(in srgb, #ff7043 16%, transparent);
    color: #ff7043;
    border-color: color-mix(in srgb, #ff7043 30%, transparent);
  }

  &.is-low {
    background: rgba(255, 255, 255, 0.06);
    color: $grey-5;
    border-color: rgba(255, 255, 255, 0.1);
  }

  &.is-mandatory {
    background: color-mix(in srgb, var(--q-warning) 18%, transparent);
    color: var(--q-warning);
    border-color: color-mix(in srgb, var(--q-warning) 35%, transparent);
  }
}

.body--light .notification-card__chip.is-low {
  background: rgba(0, 0, 0, 0.05);
  color: $grey-7;
  border-color: rgba(0, 0, 0, 0.08);
}

.notification-card__actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.notification-card__icon-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  opacity: 0.5;
  cursor: pointer;
  transition:
    opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.08);
  }

  .q-icon {
    font-size: 1rem;
  }
}

.body--light .notification-card__icon-btn:hover {
  background: rgba(0, 0, 0, 0.06);
}

.notification-card__chevron.is-rotated {
  transform: rotate(180deg);
}

.notification-card__details {
  padding: 0 12px 10px 50px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.78rem;
}

.notification-card__message {
  color: $grey-5;
  line-height: 1.4;
}

.body--light .notification-card__message {
  color: $grey-8;
}

.notification-card__progress {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.notification-card__progress-stage,
.notification-card__progress-pct,
.notification-card__progress-msg {
  font-size: 0.7rem;
  color: $grey-6;
}

.notification-card__progress-msg {
  color: $grey-7;
}

.notification-card__progress-state {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  margin-top: 4px;

  .q-icon {
    font-size: 0.9rem;
  }

  &.is-completed {
    color: var(--q-positive);
  }

  &.is-failed {
    color: var(--q-negative);
  }
}

.notification-card__files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.notification-card__file-btn {
  border-radius: 6px;
  padding: 2px 8px;
  background: rgba(255, 255, 255, 0.05);
  color: inherit;
  opacity: 0.85;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.08);
  }
}

.body--light .notification-card__file-btn {
  background: rgba(0, 0, 0, 0.04);

  &:hover {
    background: rgba(0, 0, 0, 0.07);
  }
}

.notification-card__action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
</style>
